import { FC, ChangeEvent, useState, ReactNode } from 'react';
// import { format } from 'date-fns';
// import numeral from 'numeral';
import PropTypes from 'prop-types';
import {
  Tooltip,
  Divider,
  Box,
  FormControl,
  InputLabel,
  Card,
  Checkbox,
  IconButton,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  Select,
  MenuItem,
  Typography,
  useTheme,
  CardHeader
} from '@mui/material';

// import Label from 'src/components/Label';
import {
  CryptoOrder as any,
  CryptoOrderStatus
  //   UserRolename
} from 'src/models/crypto_order';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';

import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import BulkActions from './BulkActions';
import { ITableColumn, TableProps } from '../../../interfaces';
import DeleteConfirm from '../InplaceConfirm/DeleteConfirm';

interface Filters {
  status?: CryptoOrderStatus;
}

const applyFilters = (cryptoOrders: any[], filters: Filters): any[] => {
  return cryptoOrders.filter((cryptoOrder) => {
    let matches = true;

    if (filters.status && cryptoOrder.status !== filters.status) {
      matches = false;
    }

    return matches;
  });
};

const applyPagination = (
  cryptoOrders: any[],
  page: number,
  limit: number
): any[] => {
  return cryptoOrders.slice(page * limit, page * limit + limit);
};

const Table: FC<TableProps> = ({
  data: rows,
  columns,
  filterDropdown,
  showCheckbox,
  title = '',
  isLoading = false,
  isEmpty = false,
  tablePagination: {
    page = 1,
    count = 1,
    limit = 10,
    handlePageChange = () => null,
    handleLimitChange = () => null,
    limitOptions = [5, 10, 25, 30],
    showPagination = false
  },
  onEdit = () => null,
  onDelete = () => null,
  onView = () => null,
  onRow = () => null
}) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const selectedBulkActions = selectedRows.length > 0;
  //   const [page, setPage] = useState<number>(0);
  //   const [limit, setLimit] = useState<number>(5);
  const [filters, setFilters] = useState<Filters>({
    status: null
  });

  const handleStatusChange = (e: ChangeEvent<HTMLInputElement>): void => {
    let value = null;

    if (e.target.value !== 'all') {
      value = e.target.value;
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      status: value
    }));
  };

  const handleSelectAllCryptoOrders = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    setSelectedRows(
      event.target.checked ? rows.map((cryptoOrder) => cryptoOrder.id) : []
    );
  };

  const handleSelectOneCryptoOrder = (
    event: ChangeEvent<HTMLInputElement>,
    cryptoOrderId: string
  ): void => {
    if (!selectedRows.includes(cryptoOrderId)) {
      setSelectedRows((prevSelected) => [...prevSelected, cryptoOrderId]);
    } else {
      setSelectedRows((prevSelected) =>
        prevSelected.filter((id) => id !== cryptoOrderId)
      );
    }
  };

  const onPageChange = (event: any, newPage: number): void => {
    // setPage(newPage);
    handlePageChange(newPage);
  };

  const onRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>): void => {
    // setLimit(parseInt(event.target.value));
    handleLimitChange(+event.target.value);
  };

  //   const filteredCryptoOrders = applyFilters(rows, filters);
  //   const paginatedRows = applyPagination(
  //     rows,
  //     page,
  //     limit
  //   );
  const selectedSomeCryptoOrders =
    selectedRows.length > 0 && selectedRows.length < rows.length;
  const selectedAllCryptoOrders = selectedRows.length === rows.length;
  const theme = useTheme();

  const getActions = (action: string, row: any) => {
    const actions = {
      edit: (
        <Tooltip title="Edit" arrow>
          <IconButton
            sx={{
              '&:hover': {
                background: theme.colors.primary.lighter
              },
              color: theme.palette.primary.main
            }}
            color="inherit"
            size="small"
            onClick={() => onEdit(row)}
          >
            <EditTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      view: (
        <Tooltip title="View" arrow>
          <IconButton
            sx={{
              '&:hover': {
                background: theme.colors.primary.lighter
              },
              color: theme.palette.primary.main
            }}
            color="inherit"
            size="small"
            onClick={() => onView(row)}
          >
            <VisibilityTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      delete: (
        <Tooltip title="Delete" arrow>
          <DeleteConfirm confirm={() => onDelete(row)} disabled={false} />
        </Tooltip>
      )
    };

    return actions[action] ? actions[action] : '';
  };

  return (
    <Card>
      {selectedBulkActions && (
        <Box flex={1} p={2}>
          <BulkActions />
        </Box>
      )}
      {!selectedBulkActions && (
        <CardHeader
          action={
            <Box width={150}>
              {filterDropdown && (
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || 'all'}
                    onChange={handleStatusChange}
                    label={filterDropdown.label}
                    autoWidth
                  >
                    {filterDropdown.options.map((statusOption) => (
                      <MenuItem key={statusOption.id} value={statusOption.id}>
                        {statusOption.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          }
          title={title}
        />
      )}
      <Divider />
      <TableContainer>
        <MuiTable>
          <TableHead>
            <TableRow>
              {showCheckbox ? (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={selectedAllCryptoOrders}
                    indeterminate={selectedSomeCryptoOrders}
                    onChange={handleSelectAllCryptoOrders}
                  />
                </TableCell>
              ) : (
                ''
              )}
              {columns.map((col: ITableColumn) => (
                <TableCell key={col.id} align={col.align}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : isEmpty || !rows.length ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No data found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const isRowSelected = selectedRows.includes(row.id);
                return (
                  <TableRow hover className="hand" onClick={()=>onRow(row)} key={row.id} selected={isRowSelected}>
                    {showCheckbox ? (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isRowSelected}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleSelectOneCryptoOrder(event, row.id)
                          }
                          value={isRowSelected}
                        />
                      </TableCell>
                    ) : (
                      ''
                    )}
                    {columns.map(
                      ({
                        id,
                        align,
                        accessor,
                        isAction,
                        actions
                      }: ITableColumn) => (
                        <TableCell key={id} align={align}>
                          {isAction ? (
                            <>
                              {actions
                                ? actions.map((action: string, idx: number) => getActions(action, row))
                                : null}
                            </>
                          ) : (
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color="text.primary"
                              gutterBottom
                              noWrap
                            >
                              {typeof accessor === 'string'
                                ? row[accessor]
                                : accessor(row)}
                            </Typography>
                          )}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {showPagination ? (
        <Box p={2}>
          <TablePagination
            component="div"
            count={count}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            page={page - 1}
            rowsPerPage={limit}
            rowsPerPageOptions={limitOptions}
          />
        </Box>
      ) : (
        ''
      )}
    </Card>
  );
};

Table.propTypes = {
  data: PropTypes.array.isRequired
};

Table.defaultProps = {
  data: []
};

export default Table;
